import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faPlaneArrival,
  faPlaneDeparture,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import {
  addDays,
  differenceInCalendarDays,
  format,
  isSameMonth,
  isToday,
  startOfDay,
} from 'date-fns';
import React, { useMemo } from 'react';
import { CalendarEventOrganiser } from '~/components/PlannerView/CalendarEventOrganiser';
import DraggableJobWrapper from '~/components/PlannerView/DraggableJobWrapper';
import { ScheduleEventPill } from '~/components/PlannerView/ScheduleEventPill';
import { formatTime } from '~/helpers/formatDate';
import { getScheduleEventCompositeKey } from '~/helpers/scheduleEventKey';
import type { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import type { ZoomLevel } from '~/routes/calendar._index';
import { CalendarDateCell } from './CalendarDateCell';
import {
  DATE_NUMBER_AREA_PX,
  JOB_PILL_HEIGHT_PX,
  JOB_PILL_MARGIN_PX,
  MONTHLY_DATE_NUMBER_AREA,
  MONTHLY_JOB_PILL_HEIGHT,
  MONTHLY_JOB_PILL_MARGIN,
  RESERVED_BOTTOM_SPACE_PX,
} from './constants';
import type { WeekEventPositions } from './hooks/useCalendarEventPositions';
import type {
  BaseCellData,
  BookingSegment,
  CalendarEventWithClip,
  Job,
  PlaceData,
  RescheduleDetails,
  UrlJobCreation,
} from './types';
import { createCellKey } from './utils';

type VirtualizedWeekProps = {
  weekStartDate: Date;
  placeData: PlaceData;
  weekCalendarJobs: PlaceData['calendar'];
  onJobClick: (jobId: string, status?: string) => void;
  onReschedule?: (details: RescheduleDetails) => void;
  navigate: (path: string) => void;
  storage: ReturnType<typeof useLocalStorage>;
  currentMonth: Date;
  rowHeight: number;
  jobsByDate: Map<string, Job[]>;
  bookingsHidden: boolean;
  draftCellId: string | null;
  highlightedCells: Set<string>;
  setDraftCellId: React.Dispatch<React.SetStateAction<string | null>>;
  setHighlightedCells: React.Dispatch<React.SetStateAction<Set<string>>>;
  urlJobCreation: UrlJobCreation;
  draftJobName: string | null;
  hiddenButtonCell: { siteId: string; date: string } | null;
  setHiddenButtonCell: React.Dispatch<
    React.SetStateAction<{ siteId: string; date: string } | null>
  >;
  setIsInJobCreationFlow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDayViewDate: string | null;
  /** Pre-computed event positions from parent - KEY PERFORMANCE OPTIMIZATION */
  precomputedPositions?: WeekEventPositions;
};

/**
 * Virtualized week row component
 *
 * PERFORMANCE: This component now receives pre-computed event positions
 * from the parent via `precomputedPositions` prop. This eliminates the
 * need to create CalendarEventOrganiser instances per week row.
 *
 * Fallback: If precomputedPositions is not provided, computes locally
 * (for backwards compatibility during transition).
 */
/* eslint-disable react/prop-types */
const VirtualizedWeek = React.memo<VirtualizedWeekProps>(
  ({
    weekStartDate,
    placeData,
    weekCalendarJobs,
    onJobClick,
    onReschedule,
    navigate,
    storage,
    currentMonth,
    rowHeight,
    jobsByDate,
    bookingsHidden,
    draftCellId,
    highlightedCells,
    setDraftCellId,
    setHighlightedCells,
    urlJobCreation,
    draftJobName,
    hiddenButtonCell,
    setHiddenButtonCell,
    setIsInJobCreationFlow,
    selectedDayViewDate,
    precomputedPositions,
  }) => {
    const { isTablet, isMobile } = useBreakpoint();

    // Use pre-computed positions if available, otherwise compute locally (fallback)
    const { weekEvents, eventVpos, bookingVpos, bookingLaneCount } =
      useMemo(() => {
        // FAST PATH: Use pre-computed positions from parent
        if (precomputedPositions) {
          return precomputedPositions;
        }

        // SLOW PATH (fallback): Compute locally
        // This path is only used during transition or if parent doesn't provide positions
        const events: CalendarEventWithClip[] = [];
        const calendarJobs = weekCalendarJobs ?? [];
        const weekStartDay = startOfDay(weekStartDate);
        const weekEndDay = startOfDay(addDays(weekStartDate, 6));

        calendarJobs.forEach((job, eventIndex) => {
          if (!job.start) return;
          if (job.status === 'Booking' && bookingsHidden) return;

          const jobStartDay = startOfDay(new Date(job.start));
          const jobEndDay = job.end
            ? startOfDay(new Date(job.end))
            : jobStartDay;
          const isBooking = job.status === 'Booking';
          const effectiveEndDay =
            isBooking && jobEndDay > jobStartDay
              ? addDays(jobEndDay, -1)
              : jobEndDay;

          // Skip if no occupied night overlaps this week
          if (effectiveEndDay < weekStartDay || jobStartDay > weekEndDay)
            return;

          const clampedStart =
            jobStartDay < weekStartDay ? weekStartDay : jobStartDay;
          // For DISPLAY, use the original checkout date (jobEndDay) so pills visually end on checkout day.
          // effectiveEndDay is only used for overlap/clip/lane calculations.
          const clampedEnd = jobEndDay > weekEndDay ? weekEndDay : jobEndDay;

          const columnIndex = Math.max(
            0,
            differenceInCalendarDays(clampedStart, weekStartDay)
          );

          // Clip detection: use original checkout date (jobEndDay) for bookings
          // so cross-week bookings are visually clipped correctly.
          const clip = {
            left: jobStartDay < weekStartDay,
            right: jobEndDay > weekEndDay,
          };

          events.push({
            y: 0,
            x: columnIndex,
            event: {
              id: job.id,
              status: job.status,
              assignee: job.assignee,
              included: null,
              attributes: job.attributes,
              tags: null,
              summary: job.summary,
              start: clampedStart.toISOString(),
              end: clampedEnd.toISOString(),
              scheduledStartTime: job.scheduledStartTime,
              scheduledEndTime: job.scheduledEndTime,
              timeZone: job.timeZone,
              filtered: job.filtered ?? false,
            },
            clip,
            eventIndex,
            siteId: placeData?.id || '',
            compositeKey: getScheduleEventCompositeKey(job, placeData?.id),
          } as CalendarEventWithClip);
        });

        // Calculate positions locally (fallback)
        const nonBookingEvents = events.filter(
          (e) => e.event.status !== 'Booking'
        );
        const organiser = new CalendarEventOrganiser({
          events: nonBookingEvents as any,
          zoomLevel: 'month' as ZoomLevel,
        });

        const bookings = events
          .filter((e) => e.event.status === 'Booking')
          .map((e) => {
            const origStart = new Date(e.event.start);
            const origEnd = e.event.end ? new Date(e.event.end) : origStart;
            // Half-open interval for lane calculation only: [start, end)
            // This avoids treating back-to-back bookings as overlapping.
            const adjustedEnd =
              origEnd > origStart ? addDays(origEnd, -1) : origStart;
            return {
              ...e,
              event: {
                ...e.event,
                status: 'Created',
                start: origStart.toISOString(),
                end: adjustedEnd.toISOString(),
              },
            };
          });

        let bVpos: Record<string, number> = {};
        let bLaneCount = 0;
        if (bookings.length > 0) {
          const bOrg = new CalendarEventOrganiser({
            events: bookings as any,
            zoomLevel: 'month' as ZoomLevel,
          });
          bVpos = bOrg.getEventVpos();
          const vals = Object.values(bVpos);
          bLaneCount = vals.length > 0 ? Math.max(...vals) + 1 : 0;
        }

        return {
          weekEvents: events,
          eventVpos: organiser.getEventVpos(),
          bookingVpos: bVpos,
          bookingLaneCount: bLaneCount,
        };
      }, [
        precomputedPositions,
        weekCalendarJobs,
        weekStartDate,
        bookingsHidden,
        placeData?.id,
      ]);

    // Booking overlay height
    const BOOKING_PILL_HEIGHT = MONTHLY_JOB_PILL_HEIGHT;
    const BOOKING_PILL_MARGIN = MONTHLY_JOB_PILL_MARGIN;
    const bookingOverlayHeightPx = useMemo(
      () => bookingLaneCount * (BOOKING_PILL_HEIGHT + BOOKING_PILL_MARGIN),
      [bookingLaneCount]
    );

    // Build booking segments for split rendering
    const bookingSegments = useMemo<BookingSegment[]>(() => {
      const segments: BookingSegment[] = [];
      const weekStart = weekStartDate;
      const weekEnd = addDays(weekStartDate, 6);

      weekEvents
        .filter((e) => e.event.status === 'Booking')
        .forEach((calendarEvent) => {
          const { clip, event, compositeKey } = calendarEvent;
          const bookingKey = compositeKey || event.id;
          const eventStart = new Date(event.start);
          const eventEnd = event.end ? new Date(event.end) : eventStart;

          const segStart = eventStart < weekStart ? weekStart : eventStart;
          const segEnd = eventEnd > weekEnd ? weekEnd : eventEnd;

          const startCol = Math.max(
            0,
            Math.min(
              6,
              differenceInCalendarDays(
                startOfDay(segStart),
                startOfDay(weekStartDate)
              )
            )
          );
          const endCol = Math.max(
            0,
            Math.min(
              6,
              differenceInCalendarDays(
                startOfDay(segEnd),
                startOfDay(weekStartDate)
              )
            )
          );

          for (let col = startCol; col <= endCol; col++) {
            segments.push({
              event,
              compositeKey: bookingKey,
              dayCol: col,
              startCol,
              endCol,
              vpos: bookingVpos[bookingKey] ?? 0,
              leftClipped: clip?.left === true,
              rightClipped: clip?.right === true,
            });
          }
        });

      return segments;
    }, [weekEvents, bookingVpos, weekStartDate]);

    // Days with bookings
    const daysWithBooking = useMemo(() => {
      const set = new Set<number>();
      bookingSegments.forEach((s) => set.add(s.dayCol));
      return set;
    }, [bookingSegments]);

    // Boundary days for split bookings (where one booking ends and another starts)
    // NOTE: We must check leftClipped/rightClipped to avoid false positives when a booking
    // is clipped at the week boundary. A left-clipped booking doesn't truly "start" on Monday,
    // and a right-clipped booking doesn't truly "end" on Sunday.
    const boundaryDays = useMemo(() => {
      const days = new Set<number>();
      for (let col = 0; col < 7; col++) {
        const starts = new Set<string>();
        const ends = new Set<string>();
        bookingSegments.forEach((s) => {
          if (s.dayCol !== col) return;
          // Only count as "starting" if it actually starts on this day (not left-clipped)
          if (s.dayCol === s.startCol && !s.leftClipped) starts.add(s.event.id);
          // Only count as "ending" if it actually ends on this day (not right-clipped)
          if (s.dayCol === s.endCol && !s.rightClipped) ends.add(s.event.id);
        });
        let hasPair = false;
        ends.forEach((eId) => {
          starts.forEach((sId) => {
            if (eId !== sId) hasPair = true;
          });
        });
        if (hasPair) days.add(col);
      }
      return days;
    }, [bookingSegments]);

    // Map each booking event to whether its start or end falls on a boundary day
    // NOTE: Same clip-awareness as boundaryDays - only mark start/end if not clipped
    const bookingBoundaryByEvent = useMemo(() => {
      const map: Record<string, { start: boolean; end: boolean }> = {};
      boundaryDays.forEach((col) => {
        bookingSegments.forEach((s) => {
          if (s.dayCol !== col) return;
          const entry = (map[s.event.id] = map[s.event.id] || {
            start: false,
            end: false,
          });
          // Only mark as starting on boundary if not left-clipped
          if (s.dayCol === s.startCol && !s.leftClipped) entry.start = true;
          // Only mark as ending on boundary if not right-clipped
          if (s.dayCol === s.endCol && !s.rightClipped) entry.end = true;
        });
      });
      return map;
    }, [boundaryDays, bookingSegments]);

    // Ensure same-day boundary bookings share the same lane (avoid vertical stacking)
    // Also apply to any day column with multiple booking segments (e.g., Sunday), to keep them aligned
    const boundaryLaneOverride = useMemo(() => {
      const override: Record<string, number> = {};

      // 1) Classic boundary: a checkout and checkin on the same day
      boundaryDays.forEach((col) => {
        const keys: string[] = [];
        bookingSegments.forEach((s) => {
          if (s.dayCol !== col) return;
          if (s.dayCol === s.startCol && !s.leftClipped)
            keys.push(s.compositeKey);
          if (s.dayCol === s.endCol && !s.rightClipped)
            keys.push(s.compositeKey);
        });
        if (keys.length < 2) return;
        let minLane = Number.POSITIVE_INFINITY;
        keys.forEach((key) => {
          const lane = bookingVpos[key] ?? 0;
          if (lane < minLane) minLane = lane;
        });
        if (!Number.isFinite(minLane)) minLane = 0;
        keys.forEach((key) => {
          override[key] = minLane;
        });
      });

      // 2) Fallback: any day column that has multiple booking segments (e.g., last column Sunday)
      const segmentsByCol = new Map<number, Set<string>>();
      bookingSegments.forEach((s) => {
        const set = segmentsByCol.get(s.dayCol) ?? new Set<string>();
        set.add(s.compositeKey);
        segmentsByCol.set(s.dayCol, set);
      });
      segmentsByCol.forEach((keys) => {
        if (keys.size < 2) return;
        let minLane = Number.POSITIVE_INFINITY;
        keys.forEach((key) => {
          const lane = bookingVpos[key] ?? 0;
          if (lane < minLane) minLane = lane;
        });
        if (!Number.isFinite(minLane)) minLane = 0;
        keys.forEach((key) => {
          override[key] = minLane;
        });
      });

      return override;
    }, [bookingSegments, boundaryDays, bookingVpos]);

    // Group booking ids by column for overlap handling
    // For non-Sunday columns: only track single-day spans
    // For Sunday (col 6): track ALL bookings that touch that day
    // This ensures back-to-back bookings on Sunday are split even when boundary detection doesn't apply
    const bookingIdsByCol = useMemo(() => {
      const map = new Map<number, string[]>();
      bookingSegments.forEach((s) => {
        // For the last column (Sunday), include ALL booking segments that touch it
        // This handles the edge case where multi-day checkout bookings overlap with checkin bookings
        const isLastColumn = s.dayCol === 6;
        if (!isLastColumn && s.startCol !== s.endCol) return; // only single-day spans for non-Sunday columns
        const arr = map.get(s.dayCol) ?? [];
        if (!arr.includes(s.event.id)) arr.push(s.event.id);
        map.set(s.dayCol, arr.sort());
      });
      return map;
    }, [bookingSegments]);

    // Adjusted event vpos with draft awareness
    const adjustedEventVpos = useMemo(() => {
      const result: Record<string, number> = {};
      const baseMap: Record<string, number> = {};

      weekEvents.forEach((e) => {
        if (e.event.status === 'Booking') return;
        const key = e.compositeKey || e.event.id;
        const v = eventVpos[key] as number | undefined;
        if (typeof v === 'number') baseMap[key] = v;
      });

      let draftCol: number | null = null;
      if (urlJobCreation && placeData?.id === urlJobCreation.siteId) {
        const col = differenceInCalendarDays(
          startOfDay(urlJobCreation.parsedDate),
          startOfDay(weekStartDate)
        );
        if (col >= 0 && col <= 6) draftCol = col;
      }

      weekEvents.forEach((e) => {
        if (e.event.status === 'Booking') return;
        const key = e.compositeKey || e.event.id;
        const base = baseMap[key];
        if (typeof base !== 'number') return;
        const days = CalendarEventOrganiser.getCalendarEventLength({
          start: e.event.start,
          end: e.event.end,
        });
        const startCol = e.x;
        const endCol = startCol + days - 1;
        const overlapsDraft =
          draftCol !== null && draftCol >= startCol && draftCol <= endCol;
        result[key] = base + (overlapsDraft ? 1 : 0);
      });

      // Per-column lane compaction
      for (let col = 0; col < 7; col++) {
        const eventsInCol = weekEvents.filter((e) => {
          if (e.event.status === 'Booking') return false;
          const days = CalendarEventOrganiser.getCalendarEventLength({
            start: e.event.start,
            end: e.event.end,
          });
          return col >= e.x && col <= e.x + days - 1;
        });

        eventsInCol.sort((a, b) => {
          const keyA = a.compositeKey || a.event.id;
          const keyB = b.compositeKey || b.event.id;
          const va = baseMap[keyA] ?? 0;
          const vb = baseMap[keyB] ?? 0;
          return va - vb;
        });

        const used = new Set<number>();
        let nextLane = 0;
        for (const ev of eventsInCol) {
          const key = ev.compositeKey || ev.event.id;
          const base = baseMap[key] ?? 0;
          let desired = Math.max(result[key] ?? base, nextLane);
          while (used.has(desired)) desired += 1;
          result[key] = Math.max(result[key] ?? base, desired);
          used.add(desired);
          nextLane = desired + 1;
        }
      }

      return result;
    }, [weekEvents, eventVpos, urlJobCreation, placeData?.id, weekStartDate]);

    // Min vpos event per column (for single-row display)
    const minVposEventIdByCol = useMemo(() => {
      const map: Record<number, string | null> = {};
      for (let col = 0; col < 7; col++) {
        let minV = Number.POSITIVE_INFINITY;
        let minId: string | null = null;
        weekEvents.forEach((e) => {
          if (e.event.status === 'Booking') return;
          const days = CalendarEventOrganiser.getCalendarEventLength({
            start: e.event.start,
            end: e.event.end,
          });
          const spansCol = col >= e.x && col <= e.x + days - 1;
          if (!spansCol) return;
          const key = e.compositeKey || e.event.id;
          const base = eventVpos[key] as number | undefined;
          if (base === undefined) return;
          const adjusted = adjustedEventVpos[key] ?? base;
          if (adjusted < minV) {
            minV = adjusted;
            minId = e.event.id;
          }
        });
        map[col] = minId;
      }
      return map;
    }, [weekEvents, adjustedEventVpos, eventVpos]);

    // Single-day events grouped by column (mobile)
    const singleDayEventsByCol = useMemo(() => {
      const result: Record<number, CalendarEventWithClip[]> = {
        0: [],
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: [],
      };
      weekEvents.forEach((e) => {
        if (e.event.status === 'Booking') return;
        const l = CalendarEventOrganiser.getCalendarEventLength({
          start: e.event.start,
          end: e.event.end,
        });
        if (l === 1) {
          const c = Math.max(0, Math.min(6, e.x));
          result[c].push(e);
        }
      });
      for (let c = 0; c < 7; c++) {
        result[c].sort((a, b) => (a.eventIndex ?? 0) - (b.eventIndex ?? 0));
      }
      return result;
    }, [weekEvents]);

    // Visible multi-day columns (mobile)
    const visibleMultiDayCols = useMemo(() => {
      const cols = new Set<number>();
      if (!isMobile) return cols;

      for (let col = 0; col < 7; col++) {
        const topEventId = minVposEventIdByCol[col];
        if (!topEventId) continue;

        const topEvent = weekEvents.find((e) => e.event.id === topEventId);
        if (!topEvent) continue;

        const len = CalendarEventOrganiser.getCalendarEventLength({
          start: topEvent.event.start,
          end: topEvent.event.end,
        });

        if (len > 1) {
          cols.add(col);
        }
      }

      return cols;
    }, [isMobile, minVposEventIdByCol, weekEvents]);

    // Pre-compute cell data
    const weekCellData = useMemo((): BaseCellData[] => {
      const cellsData: BaseCellData[] = [];
      for (let i = 0; i < 7; i++) {
        const date = addDays(weekStartDate, i);
        const dateKey = format(date, 'yyyy-MM-dd');
        const isWithinCurrentMonth = isSameMonth(date, currentMonth);

        cellsData.push({
          virtualWeek: null,
          date,
          dateKey,
          cellKey: createCellKey(placeData?.id || '', date),
          isCurrentMonth: isWithinCurrentMonth,
          dayJobs: jobsByDate.get(dateKey) || [],
        });
      }
      return cellsData;
    }, [weekStartDate, currentMonth, placeData?.id, jobsByDate]);

    return (
      <div className='relative grid h-full grid-cols-7 bg-white'>
        {/* Date cells */}
        {weekCellData.map((cellData) => (
          <CalendarDateCell
            key={cellData.dateKey}
            date={cellData.date}
            jobs={cellData.dayJobs}
            placeData={placeData}
            onJobClick={onJobClick}
            onReschedule={onReschedule}
            navigate={navigate}
            storage={storage}
            currentMonth={currentMonth}
            cellHeight={rowHeight}
            overlayTopOffsetPx={bookingsHidden ? 0 : bookingOverlayHeightPx}
            bookingsHidden={bookingsHidden}
            draftCellId={draftCellId}
            highlightedCells={highlightedCells}
            setDraftCellId={setDraftCellId}
            setHighlightedCells={setHighlightedCells}
            hiddenButtonCell={hiddenButtonCell}
            setHiddenButtonCell={setHiddenButtonCell}
            setIsInJobCreationFlow={setIsInJobCreationFlow}
            selectedDayViewDate={selectedDayViewDate}
          />
        ))}

        {/* Day numbers overlay */}
        <div className='pointer-events-none absolute left-0 right-0 top-0 z-[10] grid grid-cols-7'>
          {weekCellData.map((cellData, dayColIdx) => {
            const isInSelectedMonth = isSameMonth(cellData.date, currentMonth);
            const isToday_ = isToday(cellData.date);
            const isBookingDay =
              !bookingsHidden && daysWithBooking.has(dayColIdx);
            return (
              <div key={`dn-${cellData.dateKey}`} className='p-1 pl-0 pt-0'>
                <div
                  className={classNames(
                    'mb-1 ml-0.5 mt-0.5 flex items-center justify-start text-sm',
                    {
                      'text-white': isBookingDay,
                      'text-blue-600 font-semibold':
                        !isBookingDay && isToday_ && isInSelectedMonth,
                      'text-gray-900':
                        !isBookingDay && isInSelectedMonth && !isToday_,
                      'text-gray-500': !isBookingDay && !isInSelectedMonth,
                    }
                  )}
                >
                  <div className='flex h-6 w-5 items-center justify-center rounded-full text-sm leading-4'>
                    {format(cellData.date, 'd')}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Booking pills overlay */}
        {!bookingsHidden && bookingLaneCount > 0 && (
          <div
            className='absolute left-0 right-0 top-0 z-[6]'
            style={{ height: bookingOverlayHeightPx }}
          >
            {weekEvents
              .filter((e) => e.event.status === 'Booking')
              .map((calendarEvent) => {
                const { event, compositeKey } = calendarEvent;
                const bookingKey = compositeKey || event.id;
                const weekStart = weekStartDate;
                const weekEnd = addDays(weekStartDate, 6);
                const eventStart = new Date(event.start);
                const eventEnd = event.end ? new Date(event.end) : eventStart;

                const segStart =
                  eventStart < weekStart ? weekStart : eventStart;
                const segEnd = eventEnd > weekEnd ? weekEnd : eventEnd;
                const segmentStartCol = Math.max(
                  0,
                  Math.min(
                    6,
                    differenceInCalendarDays(
                      startOfDay(segStart),
                      startOfDay(weekStartDate)
                    )
                  )
                );
                const segmentEndCol = Math.max(
                  0,
                  Math.min(
                    6,
                    differenceInCalendarDays(
                      startOfDay(segEnd),
                      startOfDay(weekStartDate)
                    )
                  )
                );
                const daysWithin = Math.max(
                  1,
                  segmentEndCol - segmentStartCol + 1
                );

                const pillWidthPercent = (daysWithin / 7) * 100;
                const leftOffsetPercent = (segmentStartCol / 7) * 100;

                const leftClipped = calendarEvent.clip?.left === true;
                const rightClipped = calendarEvent.clip?.right === true;
                const showCheckIn = !leftClipped;
                const showCheckOut = !rightClipped;

                const vpos =
                  boundaryLaneOverride[bookingKey] ??
                  bookingVpos[bookingKey] ??
                  0;
                const topPx =
                  vpos * (BOOKING_PILL_HEIGHT + BOOKING_PILL_MARGIN);

                const cellIsInCurrentMonth =
                  isSameMonth(eventStart, currentMonth) ||
                  isSameMonth(eventEnd, currentMonth);

                // Calculate start/end column indices for this booking segment
                const startColIdx = segmentStartCol;
                const endColIdx = segmentEndCol;

                // Apply boundary day split adjustments
                // When checkout/checkin happen on the same day, split the cell in half
                let renderLeftPercent = leftOffsetPercent;
                let renderWidthPercent = pillWidthPercent;
                const boundaryInfo = bookingBoundaryByEvent[event.id];
                if (boundaryInfo) {
                  const GAP_DAYS = -0.005; // hairline gap to avoid overlap
                  const trimStartDays =
                    boundaryInfo.start && boundaryDays.has(startColIdx)
                      ? 0.5 - GAP_DAYS
                      : 0;
                  const trimEndDays =
                    boundaryInfo.end && boundaryDays.has(endColIdx)
                      ? 0.5 - GAP_DAYS
                      : 0;

                  // Apply symmetric trimming so checkout/checkin on same day split the cell
                  const leftTrimPercent = (trimStartDays / 7) * 100;
                  const totalTrimPercent =
                    ((trimStartDays + trimEndDays) / 7) * 100;

                  renderLeftPercent += leftTrimPercent;
                  renderWidthPercent = Math.max(
                    0,
                    renderWidthPercent - totalTrimPercent
                  );
                } else if (daysWithin === 1) {
                  // Fallback for single-day bookings: split evenly if multiple bookings share the column
                  const siblings = bookingIdsByCol.get(startColIdx) ?? [];
                  if (siblings.length > 1) {
                    const idx = siblings.indexOf(event.id);
                    const slice = pillWidthPercent / siblings.length;
                    renderLeftPercent = leftOffsetPercent + slice * idx;
                    renderWidthPercent = slice;
                  }
                } else if (endColIdx === 6) {
                  // Fallback for multi-day bookings ending on Sunday (last column)
                  // If multiple bookings share Sunday but boundary split wasn't applied,
                  // trim the checkout booking to leave room for checkin booking
                  const sundaySiblings = bookingIdsByCol.get(6) ?? [];
                  if (sundaySiblings.length > 1) {
                    // Find this booking's segment for Sunday to check if it ends here
                    const thisSegment = bookingSegments.find(
                      (s) => s.event.id === event.id && s.dayCol === 6
                    );
                    if (
                      thisSegment &&
                      thisSegment.dayCol === thisSegment.endCol
                    ) {
                      // This booking ends on Sunday - take the left half of Sunday
                      const sundayWidthPercent = (1 / 7) * 100;
                      const halfSunday = sundayWidthPercent / 2;
                      renderWidthPercent =
                        pillWidthPercent - sundayWidthPercent + halfSunday;
                    }
                  }
                }

                // Hide the check-in plane icon when this booking starts on a boundary
                // day (i.e., back-to-back checkout/checkin) to reduce visual clutter.
                const hideCheckInIcon =
                  boundaryInfo?.start && boundaryDays.has(startColIdx);
                const hideCheckOutIcon =
                  showCheckOut && Boolean(boundaryInfo?.end);

                const checkInMarginClass =
                  boundaryInfo?.start && boundaryDays.has(startColIdx)
                    ? 'ml-1'
                    : 'ml-7';

                const isOwnerBooking =
                  event.attributes?.some(
                    (attr: { name?: string; value?: unknown }) =>
                      attr?.name === 'Owner' && Boolean(attr.value)
                  ) ?? false;
                const bookingBgClass = isOwnerBooking
                  ? 'bg-gray-500'
                  : 'bg-primary-container';

                return (
                  <div
                    key={`booking-${event.id}`}
                    className={classNames(
                      'absolute overflow-hidden rounded-full',
                      leftClipped && 'rounded-l-none',
                      rightClipped && 'rounded-r-none',
                      !cellIsInCurrentMonth && 'bg-gray-400'
                    )}
                    style={{
                      left: `${renderLeftPercent}%`,
                      width: `${renderWidthPercent}%`,
                      top: topPx,
                      height: BOOKING_PILL_HEIGHT,
                      opacity: cellIsInCurrentMonth ? 1 : 0.4,
                    }}
                  >
                    <div
                      role='button'
                      tabIndex={0}
                      aria-label={`Booking: ${event.summary}`}
                      onClick={() => navigate(`bookings/${event.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`bookings/${event.id}`);
                        }
                      }}
                      className={classNames(
                        'flex h-full w-full items-center justify-between overflow-hidden px-2 text-xs leading-4 text-white',
                        bookingBgClass,
                        'focus:outline-none focus:ring-2 focus:ring-[#298888]/60 focus:ring-offset-1',
                        'rounded-full',
                        leftClipped && 'rounded-l-none',
                        rightClipped && 'rounded-r-none',
                        Boolean(event.filtered) && 'opacity-50'
                      )}
                      title={event.summary}
                    >
                      {showCheckIn && event.scheduledStartTime ? (
                        <span
                          className={classNames(
                            checkInMarginClass,
                            'mr-2 hidden shrink-0 items-center gap-1 lg:flex'
                          )}
                        >
                          {!hideCheckInIcon && (
                            <FontAwesomeIcon
                              icon={faPlaneArrival as IconProp}
                            />
                          )}
                          {formatTime(event.scheduledStartTime)}
                        </span>
                      ) : (
                        <span className='mr-2 w-0 shrink-0' />
                      )}
                      {showCheckOut && event.scheduledEndTime ? (
                        <span className='ml-1 mr-1 hidden shrink-0 items-center gap-1 lg:flex'>
                          {!hideCheckOutIcon && (
                            <FontAwesomeIcon
                              icon={faPlaneDeparture as IconProp}
                            />
                          )}
                          {formatTime(event.scheduledEndTime)}
                        </span>
                      ) : (
                        <span className='ml-2 w-0 shrink-0' />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Inactive-month dim overlay */}
        <div className='pointer-events-none absolute inset-0 z-[14] grid grid-cols-7'>
          {weekCellData.map((cellData) => {
            const isInSelectedMonth = isSameMonth(cellData.date, currentMonth);
            return (
              <div
                key={`dim-${cellData.dateKey}`}
                className={classNames(
                  'h-full w-full transition-opacity duration-100',
                  {
                    'opacity-0': isInSelectedMonth,
                    'opacity-100': !isInSelectedMonth,
                  }
                )}
              >
                <div className='h-full w-full bg-gray-100/70' />
              </div>
            );
          })}
        </div>

        {/* Job pills */}
        {weekEvents.map((calendarEvent, i) => {
          const { x: columnIndex, event, siteId, compositeKey } = calendarEvent;
          const eventKey = compositeKey || event.id;
          const baseVpos = eventVpos[eventKey] as number | undefined;

          if (baseVpos === undefined) return null;
          if (event.status === 'Booking') return null;

          const days = CalendarEventOrganiser.getCalendarEventLength({
            start: event.start,
            end: event.end,
          });

          const adjustedVpos = adjustedEventVpos[eventKey];
          const cellHeight = rowHeight;
          const pillHeight = isMobile ? 20 : JOB_PILL_HEIGHT_PX;
          const availableHeightForJobs =
            cellHeight - RESERVED_BOTTOM_SPACE_PX - DATE_NUMBER_AREA_PX;
          const jobsPerPill = pillHeight + JOB_PILL_MARGIN_PX;
          let maxVisibleJobs =
            isTablet || isMobile
              ? 1
              : Math.max(0, Math.floor(availableHeightForJobs / jobsPerPill));
          maxVisibleJobs = Math.max(1, maxVisibleJobs);

          if (adjustedVpos === undefined) return null;
          const forceShowSingle =
            maxVisibleJobs === 1 &&
            minVposEventIdByCol[columnIndex] === event.id;
          const shouldHideForLimit =
            adjustedVpos >= maxVisibleJobs && !forceShowSingle;
          const isSingleDayEvent = days === 1;

          if (
            (!isMobile && shouldHideForLimit) ||
            (isMobile && !isSingleDayEvent && shouldHideForLimit)
          ) {
            return null;
          }

          let pillWidthPercent = days > 1 ? (days / 7) * 100 : (1 / 7) * 100;
          let leftOffsetPercent = (columnIndex / 7) * 100;

          if (isMobile && days === 1) {
            const siblings = singleDayEventsByCol[columnIndex] || [];
            const idx = siblings.findIndex((s) => s.event.id === event.id);
            const count = Math.max(1, siblings.length);
            const dayWidthPercent = (1 / 7) * 100;
            pillWidthPercent = dayWidthPercent / count;
            leftOffsetPercent =
              (columnIndex / 7) * 100 + (dayWidthPercent * idx) / count;
          }

          let finalVpos = adjustedVpos;
          if (
            maxVisibleJobs === 1 &&
            minVposEventIdByCol[columnIndex] === event.id
          ) {
            finalVpos = 0;
          }

          if (isMobile) {
            if (days > 1) {
              finalVpos = 0;
            } else {
              if (visibleMultiDayCols.has(columnIndex)) {
                return null;
              }
              finalVpos = 0;
            }
          }

          if (finalVpos >= maxVisibleJobs) {
            return null;
          }

          const clip = calendarEvent.clip || { left: false, right: false };
          const isMulti = days > 1;
          const marginLeftPx = isMulti ? (clip.left ? 0 : 2) : isMobile ? 1 : 2;
          let marginRightPx = isMulti ? (clip.right ? 0 : 2) : isMobile ? 1 : 2;

          const dayDate = addDays(weekStartDate, columnIndex);
          const dayKey = format(dayDate, 'yyyy-MM-dd');
          const jobsForDay = jobsByDate.get(dayKey) || [];
          const nonBookingJobsForDay = jobsForDay.filter(
            (j) => j.status !== 'Booking'
          );
          const hasHiddenForDay = nonBookingJobsForDay.length > maxVisibleJobs;
          const reserveForPlusPx =
            !isMobile && hasHiddenForDay && maxVisibleJobs === 1 ? 28 : 0;
          marginRightPx += reserveForPlusPx;

          const hiddenMultiDayCountInCol = isMobile
            ? weekEvents.filter((we) => {
                if (we.event.status === 'Booking') return false;
                const len = CalendarEventOrganiser.getCalendarEventLength(
                  we.event
                );
                if (len <= 1) return false;
                const spans =
                  columnIndex >= we.x && columnIndex <= we.x + len - 1;
                if (!spans) return false;
                const weKey = we.compositeKey || we.event.id;
                const base = eventVpos[weKey] as number | undefined;
                const adj = adjustedEventVpos[weKey] ?? base;
                return (adj ?? 0) >= maxVisibleJobs;
              }).length
            : 0;

          return (
            <div
              key={`job-${event.id}-${i}`}
              className='pointer-events-auto absolute z-[8]'
              style={{
                left: `calc(${leftOffsetPercent}% + ${marginLeftPx}px)`,
                width: `calc(${pillWidthPercent}% - ${
                  marginLeftPx + marginRightPx
                }px)`,
                top:
                  DATE_NUMBER_AREA_PX -
                  4 +
                  finalVpos * (pillHeight + JOB_PILL_MARGIN_PX),
                height: pillHeight,
              }}
            >
              <DraggableJobWrapper
                job={{
                  id: event.id,
                  summary: event.summary,
                  status: event.status,
                  start: event.start,
                  end: event.end,
                  scheduledStartTime: event.scheduledStartTime,
                  scheduledEndTime: event.scheduledEndTime,
                  assignee: event.assignee,
                  attributes: event.attributes,
                }}
                siteId={siteId}
                onClick={(status, jobId) => onJobClick(jobId, status)}
                enableHoverDialogs={false}
                enableTooltips={true}
              >
                <ScheduleEventPill
                  event={{
                    summary: event.summary,
                    status: event.status,
                    start: event.start,
                    end: event.end,
                    scheduledStartTime: event.scheduledStartTime,
                    scheduledEndTime: event.scheduledEndTime,
                    timeZone: (event as any).timeZone ?? null,
                    assignee: event.assignee,
                    attributes: event.attributes,
                  }}
                  height={pillHeight}
                  width='100%'
                  absolutePosition={false}
                  showDates={false}
                  showTitle={!isMobile}
                  showScheduleInfo={!isMobile}
                  showAvatar={!isMobile}
                  showStatus={!isMobile}
                  selected={false}
                  inDialog={false}
                  filtered={Boolean(event.filtered)}
                  zoomLevel={'month' as ZoomLevel}
                  edgeClip={{
                    left: calendarEvent.clip?.left === true,
                    right: calendarEvent.clip?.right === true,
                  }}
                />
                {isMobile && days > 1 && hiddenMultiDayCountInCol > 0 && (
                  <div className='pointer-events-none absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-white'>
                    +{hiddenMultiDayCountInCol}
                  </div>
                )}
              </DraggableJobWrapper>
            </div>
          );
        })}

        {/* Draft job pill */}
        {urlJobCreation &&
          placeData?.id === urlJobCreation.siteId &&
          (() => {
            const draftCol = differenceInCalendarDays(
              startOfDay(urlJobCreation.parsedDate),
              startOfDay(weekStartDate)
            );
            if (draftCol < 0 || draftCol > 6) return null;

            const pillWidthPercent = (1 / 7) * 100;
            const leftOffsetPercent = (draftCol / 7) * 100;
            return (
              <div
                key={`draft-pill-${placeData?.id}`}
                className='pointer-events-auto absolute z-[15]'
                style={{
                  left: `calc(${leftOffsetPercent}% + 2px)`,
                  width: `calc(${pillWidthPercent}% - 4px)`,
                  top: MONTHLY_DATE_NUMBER_AREA - 2,
                  height: MONTHLY_JOB_PILL_HEIGHT,
                }}
                role='button'
                tabIndex={0}
                aria-label={`Draft job: ${draftJobName?.trim() || 'New Job'}`}
              >
                <div
                  className={classNames(
                    'border-0.5 border-status-stroke-created bg-status-created text-gray-900',
                    'group flex h-full flex-col items-stretch justify-center overflow-hidden text-[11px] leading-4',
                    'rounded-md px-2',
                    'ring-1 ring-brand-lighter'
                  )}
                  title={draftJobName?.trim() || 'New Job'}
                >
                  <div className='flex w-full items-center justify-between'>
                    <p className='min-w-0 truncate font-medium'>
                      {draftJobName?.trim() || 'New Job'}
                    </p>
                    <span className='pl-2 text-[10px]'>Creating…</span>
                  </div>
                </div>
              </div>
            );
          })()}
      </div>
    );
  },
  // Memo comparison
  (prevProps, nextProps) => {
    return (
      prevProps.weekStartDate.getTime() === nextProps.weekStartDate.getTime() &&
      isSameMonth(prevProps.currentMonth, nextProps.currentMonth) &&
      prevProps.rowHeight === nextProps.rowHeight &&
      prevProps.placeData?.id === nextProps.placeData?.id &&
      prevProps.jobsByDate === nextProps.jobsByDate &&
      prevProps.weekCalendarJobs === nextProps.weekCalendarJobs &&
      prevProps.bookingsHidden === nextProps.bookingsHidden &&
      prevProps.onJobClick === nextProps.onJobClick &&
      prevProps.onReschedule === nextProps.onReschedule &&
      prevProps.navigate === nextProps.navigate &&
      prevProps.storage === nextProps.storage &&
      prevProps.draftCellId === nextProps.draftCellId &&
      prevProps.highlightedCells === nextProps.highlightedCells &&
      prevProps.hiddenButtonCell === nextProps.hiddenButtonCell &&
      prevProps.setDraftCellId === nextProps.setDraftCellId &&
      prevProps.setHighlightedCells === nextProps.setHighlightedCells &&
      prevProps.setHiddenButtonCell === nextProps.setHiddenButtonCell &&
      prevProps.setIsInJobCreationFlow === nextProps.setIsInJobCreationFlow &&
      prevProps.precomputedPositions === nextProps.precomputedPositions &&
      (prevProps.urlJobCreation?.siteId ?? null) ===
        (nextProps.urlJobCreation?.siteId ?? null) &&
      (prevProps.urlJobCreation?.date ?? null) ===
        (nextProps.urlJobCreation?.date ?? null) &&
      (prevProps.draftJobName ?? '') === (nextProps.draftJobName ?? '') &&
      prevProps.selectedDayViewDate === nextProps.selectedDayViewDate
    );
  }
);

VirtualizedWeek.displayName = 'VirtualizedWeek';

export { VirtualizedWeek };
